/**
 * Optimized Trail Service
 *
 * Implements the multi-layer architecture for trail discovery with:
 * - Denormalized collections
 * - Path simplification
 * - Progressive loading
 * - Detail level segmentation
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  documentId,
} from 'firebase/firestore';
import { db } from '@/firebase/clientApp';
import { revalidateAfterTrailUpdate } from '@/utils/revalidationUtils';
import { TrailWaypoint } from '@/types/hikingTrail';
import {
  EnhancedTrailData,
  PointOfInterest,
  Season,
  WeatherCondition,
} from '@/types/enhancedTrail';
import {
  simplifyPath,
  createDetailLevels,
  calculateBoundingBox,
  chunkArray,
  boundingBoxesIntersect,
  BoundingBox,
} from '@/utils/pathSimplification';

/**
 * Trail summary document for fast initial load
 */
export interface TrailSummary {
  lastUpdated: number;
  trailCount: number;
  difficultyDistribution: Record<string, number>;
  seasonalTrailCounts: Record<Season, number>;
  recommendedTrails: {
    weather: Record<WeatherCondition, string[]>;
    season: Record<Season, string[]>;
  };
  topRatedTrails: string[];
}

/**
 * Detail levels for waypoint segmentation
 */
type DetailLevel = 'summary' | 'low' | 'medium' | 'high';

/**
 * A segment of waypoints for a specific detail level
 */
interface WaypointSegment {
  trailId: string;
  detailLevel: DetailLevel;
  segmentIndex?: number;
  waypoints: TrailWaypoint[];
  boundingBox: BoundingBox;
}

/**
 * Optimized Trail Discovery Service
 * Implements the multi-layer architecture for trail discovery
 */
export class OptimizedTrailService {
  // Collection paths
  private readonly trailsCollection = 'trails';
  private readonly trailSummariesCollection = 'trail_summaries';
  private readonly waypointSegmentsCollection = 'waypoint_segments';
  private readonly poisCollection = 'pois';
  private readonly waypointsCollection = 'waypoints'; // Legacy collection

  /**
   * Convert Firestore timestamp to milliseconds
   */
  private convertTimestamp(timestamp: Timestamp | null | undefined): number {
    if (!timestamp) return Date.now();
    return timestamp.toMillis();
  }

  /**
   * Generate the trail summary document with pre-computed data
   * This is a denormalized document for fast initial load
   */
  async generateTrailSummary(): Promise<void> {
    try {
      // Get all trails
      const trailsSnapshot = await getDocs(
        collection(db, this.trailsCollection)
      );
      const trails: EnhancedTrailData[] = [];

      trailsSnapshot.forEach((doc) => {
        trails.push({ id: doc.id, ...doc.data() } as EnhancedTrailData);
      });

      // Calculate summary data
      const trailCount = trails.length;
      const difficultyDistribution: Record<string, number> = {};
      const seasonalTrailCounts: Record<Season, number> = {
        spring: 0,
        summer: 0,
        autumn: 0,
        winter: 0,
      };

      // Process each trail for statistics
      trails.forEach((trail) => {
        // Count by difficulty
        const difficulty = trail.difficulty || 'unknown';
        difficultyDistribution[difficulty] =
          (difficultyDistribution[difficulty] || 0) + 1;

        // Count by season
        const seasons = trail.bestSeasons || [];
        seasons.forEach((season) => {
          seasonalTrailCounts[season] = (seasonalTrailCounts[season] || 0) + 1;
        });
      });

      // Generate recommendations by weather condition
      const weatherRecommendations: Record<WeatherCondition, string[]> = {
        sunny: [],
        cloudy: [],
        rainy: [],
        snowy: [],
        foggy: [],
        windy: [],
        light_rain: [],
        clear: [],
      };

      // Generate recommendations by season
      const seasonRecommendations: Record<Season, string[]> = {
        spring: [],
        summer: [],
        autumn: [],
        winter: [],
      };

      // Get trails best suited for each weather condition
      Object.keys(weatherRecommendations).forEach((condition) => {
        const weatherCondition = condition as WeatherCondition;
        const suitableTrails = trails
          .filter((trail) =>
            trail.weatherConsiderations?.suitableWeather.includes(
              weatherCondition
            )
          )
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 5); // Top 5 trails for each condition

        weatherRecommendations[weatherCondition] = suitableTrails.map(
          (t) => t.id
        );
      });

      // Get trails best suited for each season
      Object.keys(seasonRecommendations).forEach((season) => {
        const currentSeason = season as Season;
        const seasonalTrails = trails
          .filter((trail) => trail.bestSeasons?.includes(currentSeason))
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 5); // Top 5 trails for each season

        seasonRecommendations[currentSeason] = seasonalTrails.map((t) => t.id);
      });

      // Get top rated trails overall
      const topRatedTrails = trails
        .sort((a, b) => (b.scenicRating || 0) - (a.scenicRating || 0))
        .slice(0, 10) // Top 10 trails
        .map((t) => t.id);

      // Create the summary document
      const summaryData: TrailSummary = {
        lastUpdated: Date.now(),
        trailCount,
        difficultyDistribution,
        seasonalTrailCounts,
        recommendedTrails: {
          weather: weatherRecommendations,
          season: seasonRecommendations,
        },
        topRatedTrails,
      };

      // Save to Firestore
      await setDoc(
        doc(db, this.trailSummariesCollection, 'current'),
        summaryData
      );

      console.log('Trail summary document generated successfully');
    } catch (error) {
      console.error('Error generating trail summary:', error);
      throw error;
    }
  }

  /**
   * Generate waypoint segments for a trail at different detail levels
   * @param trailId The trail to generate segments for
   */
  async generateWaypointSegments(trailId: string): Promise<void> {
    try {
      // Get all waypoints for this trail
      const waypointsQuery = query(
        collection(db, this.waypointsCollection),
        where('trailId', '==', trailId),
        orderBy('order')
      );

      const waypointsSnapshot = await getDocs(waypointsQuery);
      const waypoints: TrailWaypoint[] = [];

      waypointsSnapshot.forEach((doc) => {
        const data = doc.data();
        waypoints.push({
          id: doc.id,
          trailId: data.trailId,
          name: data.name,
          description: data.description,
          coordinates: data.coordinates,
          order: data.order,
          timestamp: this.convertTimestamp(data.timestamp),
          photoUrl: data.photoUrl,
        });
      });

      if (waypoints.length < 2) {
        console.warn(
          `Trail ${trailId} has fewer than 2 waypoints, skipping segment generation`
        );
        return;
      }

      // Create detail levels
      const detailLevels = createDetailLevels(waypoints);

      // Store segments in batches
      const batch = writeBatch(db);

      // Store summary waypoints in trail document
      batch.update(doc(db, this.trailsCollection, trailId), {
        summaryWaypoints: detailLevels.summary,
      });

      // Store low detail level (one segment)
      batch.set(doc(db, this.waypointSegmentsCollection, `${trailId}-low-1`), {
        trailId,
        detailLevel: 'low',
        waypoints: detailLevels.low,
        boundingBox: calculateBoundingBox(detailLevels.low),
      });

      // Store medium detail level (one segment)
      batch.set(
        doc(db, this.waypointSegmentsCollection, `${trailId}-medium-1`),
        {
          trailId,
          detailLevel: 'medium',
          waypoints: detailLevels.medium,
          boundingBox: calculateBoundingBox(detailLevels.medium),
        }
      );

      // Split high detail into segments of max 100 points
      const highDetailSegments = chunkArray(detailLevels.high, 100);
      highDetailSegments.forEach((segment, index) => {
        batch.set(
          doc(
            db,
            this.waypointSegmentsCollection,
            `${trailId}-high-${index + 1}`
          ),
          {
            trailId,
            detailLevel: 'high',
            segmentIndex: index,
            waypoints: segment,
            boundingBox: calculateBoundingBox(segment),
          }
        );
      });

      // Commit all the batch operations
      await batch.commit();

      console.log(`Generated waypoint segments for trail ${trailId}`);
    } catch (error) {
      console.error(
        `Error generating waypoint segments for trail ${trailId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Generate all segments for all trails
   * This should be called once initially, and then incrementally as trails are added/updated
   */
  async generateAllWaypointSegments(): Promise<void> {
    try {
      // Get all trails
      const trailsSnapshot = await getDocs(
        collection(db, this.trailsCollection)
      );

      // Process each trail
      const trailIds: string[] = [];
      trailsSnapshot.forEach((doc) => {
        trailIds.push(doc.id);
      });

      // Process trails in sequence to avoid Firestore rate limits
      for (const trailId of trailIds) {
        await this.generateWaypointSegments(trailId);
        // Small delay to prevent hitting Firestore rate limits
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // After all trails are processed, generate the summary
      await this.generateTrailSummary();

      console.log('All waypoint segments generated successfully');

      // Trigger revalidation of all trail discovery pages
      try {
        await revalidateAfterTrailUpdate('all-trails');
      } catch (revalidateError) {
        console.error(
          'Error revalidating pages after bulk generation:',
          revalidateError
        );
      }
    } catch (error) {
      console.error('Error generating all waypoint segments:', error);
      throw error;
    }
  }

  /**
   * Get the trail summary document for fast initial load
   */
  async getTrailSummary(): Promise<TrailSummary | null> {
    try {
      const summaryDoc = await getDoc(
        doc(db, this.trailSummariesCollection, 'current')
      );

      if (!summaryDoc.exists()) {
        console.log('No trail summary found, generating...');
        await this.generateTrailSummary();
        return this.getTrailSummary(); // Recursive call after generation
      }

      return summaryDoc.data() as TrailSummary;
    } catch (error) {
      console.error('Error fetching trail summary:', error);
      throw error;
    }
  }

  /**
   * Get waypoints for a trail with the appropriate detail level based on zoom
   * @param trailId The trail ID
   * @param zoomLevel The current map zoom level
   * @param viewport Optional bounding box to only return visible segments
   */
  async getWaypointsForTrail(
    trailId: string,
    zoomLevel: number,
    viewport?: BoundingBox
  ): Promise<TrailWaypoint[]> {
    try {
      // Determine detail level based on zoom
      let detailLevel: DetailLevel = 'high';

      if (zoomLevel < 10) {
        // Use summary waypoints at low zoom
        const trailDoc = await getDoc(doc(db, this.trailsCollection, trailId));
        return trailDoc.data()?.summaryWaypoints || [];
      } else if (zoomLevel < 13) {
        detailLevel = 'low';
      } else if (zoomLevel < 15) {
        detailLevel = 'medium';
      }

      // Query for segments of the appropriate detail level
      const segmentsQuery = query(
        collection(db, this.waypointSegmentsCollection),
        where('trailId', '==', trailId),
        where('detailLevel', '==', detailLevel)
      );

      const segmentsSnapshot = await getDocs(segmentsQuery);

      // If we have viewport and are at high detail level, filter to visible segments
      if (viewport && detailLevel === 'high') {
        const visibleSegments = segmentsSnapshot.docs.filter((doc) => {
          const boundingBox = doc.data().boundingBox;
          return boundingBoxesIntersect(boundingBox, viewport);
        });

        // Combine waypoints from visible segments
        return visibleSegments
          .sort(
            (a, b) =>
              (a.data().segmentIndex || 0) - (b.data().segmentIndex || 0)
          )
          .flatMap((doc) => doc.data().waypoints);
      }

      // Combine waypoints from all segments
      return segmentsSnapshot.docs
        .sort(
          (a, b) => (a.data().segmentIndex || 0) - (b.data().segmentIndex || 0)
        )
        .flatMap((doc) => doc.data().waypoints);
    } catch (error) {
      console.error(`Error fetching waypoints for trail ${trailId}:`, error);
      throw error;
    }
  }

  /**
   * Get recommended trails for the current weather
   * @param weatherCondition The current weather condition
   * @param limit Maximum number of trails to return
   */
  async getWeatherRecommendedTrails(
    weatherCondition: WeatherCondition,
    limit: number = 5
  ): Promise<EnhancedTrailData[]> {
    try {
      // Get trail IDs from summary document
      const summary = await this.getTrailSummary();
      if (!summary) return [];

      const recommendedIds =
        summary.recommendedTrails.weather[weatherCondition] || [];

      if (recommendedIds.length === 0) return [];

      // Get the recommended trails
      const recommendedTrailsQuery = query(
        collection(db, this.trailsCollection),
        where(documentId(), 'in', recommendedIds.slice(0, limit))
      );

      const trailsSnapshot = await getDocs(recommendedTrailsQuery);
      const trails: EnhancedTrailData[] = [];

      trailsSnapshot.forEach((doc) => {
        trails.push({ id: doc.id, ...doc.data() } as EnhancedTrailData);
      });

      return trails;
    } catch (error) {
      console.error('Error fetching weather recommended trails:', error);
      throw error;
    }
  }

  /**
   * Get recommended trails for the current season
   * @param season The current season
   * @param limit Maximum number of trails to return
   */
  async getSeasonalRecommendedTrails(
    season: Season,
    limit: number = 5
  ): Promise<EnhancedTrailData[]> {
    try {
      // Get trail IDs from summary document
      const summary = await this.getTrailSummary();
      if (!summary) return [];

      const recommendedIds = summary.recommendedTrails.season[season] || [];

      if (recommendedIds.length === 0) return [];

      // Get the recommended trails
      const recommendedTrailsQuery = query(
        collection(db, this.trailsCollection),
        where(documentId(), 'in', recommendedIds.slice(0, limit))
      );

      const trailsSnapshot = await getDocs(recommendedTrailsQuery);
      const trails: EnhancedTrailData[] = [];

      trailsSnapshot.forEach((doc) => {
        trails.push({ id: doc.id, ...doc.data() } as EnhancedTrailData);
      });

      return trails;
    } catch (error) {
      console.error('Error fetching seasonal recommended trails:', error);
      throw error;
    }
  }

  /**
   * Get points of interest within the current viewport
   * @param viewport The current map viewport
   */
  async getPOIsInViewport(viewport: BoundingBox): Promise<PointOfInterest[]> {
    try {
      // Query POIs within the viewport
      // First filter by latitude (Firebase allows range queries on a single field)
      const poisQuery = query(
        collection(db, this.poisCollection),
        where('coordinates.latitude', '>=', viewport.south),
        where('coordinates.latitude', '<=', viewport.north)
      );

      const poisSnapshot = await getDocs(poisQuery);
      const pois: PointOfInterest[] = [];

      // Then filter by longitude in memory
      poisSnapshot.forEach((doc) => {
        const poiData = doc.data();
        if (
          poiData.coordinates.longitude >= viewport.west &&
          poiData.coordinates.longitude <= viewport.east
        ) {
          pois.push({
            id: doc.id,
            name: poiData.name,
            description: poiData.description,
            type: poiData.type,
            coordinates: poiData.coordinates,
            imageUrl: poiData.imageUrl,
            openingHours: poiData.openingHours,
            contactInfo: poiData.contactInfo,
            website: poiData.website,
          });
        }
      });

      return pois;
    } catch (error) {
      console.error('Error fetching POIs in viewport:', error);
      throw error;
    }
  }

  /**
   * Get a single trail by ID
   * @param trailId The trail ID to retrieve
   */
  async getTrailById(trailId: string): Promise<EnhancedTrailData | null> {
    try {
      // Get the trail document
      const trailDoc = await getDoc(doc(db, this.trailsCollection, trailId));

      if (!trailDoc.exists()) {
        return null;
      }

      const trailData = trailDoc.data();

      // Get summary waypoints directly from the trail document
      const summaryWaypoints = trailData.summaryWaypoints || [];

      // Construct the enhanced trail with all required properties
      const enhancedTrail = {
        id: trailDoc.id,
        name: trailData.name || '',
        description: trailData.description || '',
        difficulty: trailData.difficulty || 'moderate',
        totalDistance: trailData.totalDistance || 0,
        estimatedDuration: trailData.estimatedDuration || 0,
        waypoints: summaryWaypoints || [], // Include summary waypoints only initially
        createdAt: trailData.createdAt
          ? this.convertTimestamp(trailData.createdAt)
          : Date.now(),
        updatedAt: trailData.updatedAt
          ? this.convertTimestamp(trailData.updatedAt)
          : Date.now(),

        // Include any other fields from the trail document
        ...trailData,
      } as EnhancedTrailData;

      return enhancedTrail;
    } catch (error) {
      console.error(`Error fetching trail ${trailId}:`, error);
      throw error;
    }
  }

  /**
   * Get all trails with basic information (no detailed waypoints)
   */
  async getAllTrails(): Promise<EnhancedTrailData[]> {
    try {
      // Get all trails
      const trailsQuery = query(
        collection(db, this.trailsCollection),
        orderBy('name')
      );

      const trailsSnapshot = await getDocs(trailsQuery);
      const trails: EnhancedTrailData[] = [];

      trailsSnapshot.forEach((doc) => {
        const data = doc.data();
        const trail = {
          id: doc.id,
          name: data.name || '',
          description: data.description || '',
          difficulty: data.difficulty || 'moderate',
          totalDistance: data.totalDistance || 0,
          estimatedDuration: data.estimatedDuration || 0,
          waypoints: data.summaryWaypoints || [],
          createdAt: data.createdAt
            ? this.convertTimestamp(data.createdAt)
            : Date.now(),
          updatedAt: data.updatedAt
            ? this.convertTimestamp(data.updatedAt)
            : Date.now(),

          // Include any other fields from the trail document
          ...data,
        } as EnhancedTrailData;

        trails.push(trail);
      });

      return trails;
    } catch (error) {
      console.error('Error fetching all trails:', error);
      throw error;
    }
  }

  /**
   * Revalidate the denormalized data for a specific trail
   * Call this whenever a trail or its waypoints are updated
   * @param trailId The trail to revalidate
   * @param skipPageRevalidation Optional flag to skip revalidating pages (default: false)
   */
  async revalidateTrail(
    trailId: string,
    skipPageRevalidation: boolean = false
  ): Promise<void> {
    try {
      // Regenerate waypoint segments
      await this.generateWaypointSegments(trailId);

      // Update the summary (since trail data may have changed)
      await this.generateTrailSummary();

      console.log(`Trail ${trailId} data revalidated successfully`);

      // Trigger page revalidation if not skipped
      if (!skipPageRevalidation) {
        try {
          // Trigger revalidation of static pages
          await revalidateAfterTrailUpdate(trailId);
        } catch (revalidateError) {
          // Log but don't fail the whole operation if revalidation fails
          console.error(
            `Error revalidating pages for trail ${trailId}:`,
            revalidateError
          );
        }
      }
    } catch (error) {
      console.error(`Error revalidating trail ${trailId}:`, error);
      throw error;
    }
  }
}

// Export a singleton instance
export const optimizedTrailService = new OptimizedTrailService();
